import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertCircle, ChevronLeft, ChevronRight, Phone, Mail, Globe, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useFont = (family) => {
  useEffect(() => {
    if (!family) return;
    const id = `gf-${family.replace(/\s+/g, "-")}`;
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:wght@400;600;800&display=swap`;
    document.head.appendChild(link);
  }, [family]);
};

export default function Slideshow() {
  const { apotheek, cnk } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [idx, setIdx] = useState(0);

  useFont(data?.pharmacy?.lettertype);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/slideshow/${apotheek}/${cnk}`)
      .then((r) => active && (setData(r.data), setStatus("ok")))
      .catch(() => active && setStatus("notfound"));
    return () => { active = false; };
  }, [apotheek, cnk]);

  const slides = useMemo(() => {
    if (!data) return [];
    const c = data.content || {};
    // Profiel-waarschuwingen (Profielinhoud) hebben voorrang; algemene stof-waarschuwingen
    // (Werkzame stoffen) zijn de fallback wanneer er geen profiel-waarschuwingen zijn.
    const profielWs = [c.waarschuwing_1, c.waarschuwing_2, c.waarschuwing_3].filter(Boolean);
    const algemeneWs = Array.isArray(c.algemene_waarschuwingen) ? c.algemene_waarschuwingen : [];
    return [
      { type: "intro" },
      { blocks: [["Waarvoor dient dit geneesmiddel?", c.indicatie], ["Hoe werkt dit geneesmiddel?", c.werking]] },
      { blocks: [["Hoe gebruik je dit geneesmiddel?", c.gebruik], ["Dosering", c.dosering]] },
      { heading: "Waar moet je op letten?", list: profielWs.length ? profielWs : algemeneWs },
      { heading: "Welke bijwerkingen kunnen voorkomen?", blocks: [["Vaak voorkomend:", c.bijwerkingen_vaak], ["Wanneer hulp zoeken?", c.bijwerkingen_ernstig, "(komt zelden voor)"]] },
      { heading: "Extra praktische info", body: c.praktische_info },
      { type: "contact" },
    ];
  }, [data]);

  const total = slides.length;
  const next = useCallback(() => setIdx((i) => Math.min(i + 1, total - 1)), [total]);
  const prev = useCallback(() => setIdx((i) => Math.max(i - 1, 0)), []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "ArrowRight") next(); if (e.key === "ArrowLeft") prev(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  if (status === "loading")
    return <Center><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></Center>;
  if (status === "notfound")
    return (
      <Center>
        <div className="text-center text-slate-500" data-testid="slideshow-notfound">
          <AlertCircle className="mx-auto h-10 w-10 text-slate-300" />
          <h1 className="mt-3 text-xl font-semibold text-slate-700">Niet gevonden</h1>
          <p className="mt-1 max-w-xs text-sm">Deze medicatie-slideshow bestaat niet of is niet beschikbaar voor deze apotheek.</p>
        </div>
      </Center>
    );

  const { pharmacy, product } = data;
  // Huisstijl van de apotheek (uit Supabase)
  const hoofd = pharmacy.hoofdkleur;       // lichte merkkleur (decoratie)
  const bg = pharmacy.achtergrondkleur;    // slide-achtergrond
  const ink = pharmacy.tekst_kleur;        // tekst & accenten
  const font = pharmacy.lettertype ? `'${pharmacy.lettertype}', system-ui, sans-serif` : "system-ui, sans-serif";
  const body = { color: ink, opacity: 0.82 };

  const Logo = ({ big }) =>
    pharmacy.logo_url ? (
      <img src={pharmacy.logo_url} alt={pharmacy.naam} className={big ? "h-16 w-auto" : "h-8 w-auto"} />
    ) : (
      <span className={`font-bold ${big ? "text-2xl" : "text-sm"}`} style={{ color: ink }}>{pharmacy.naam}</span>
    );

  const s = slides[idx];

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 p-0 sm:p-6" style={{ fontFamily: font }}>
      <div
        className="relative flex h-[100dvh] w-full max-w-[440px] flex-col overflow-hidden sm:h-[85vh] sm:rounded-2xl sm:shadow-2xl"
        style={{ aspectRatio: "9 / 16", background: bg }}
        data-testid="slideshow-viewer"
      >
        {/* decoratieve merkkleur-blobs */}
        <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full opacity-60 blur-2xl" style={{ background: hoofd }} />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full opacity-50 blur-2xl" style={{ background: hoofd }} />

        {/* progress bars */}
        <div className="absolute left-0 right-0 top-0 z-20 flex gap-1 p-3">
          {slides.map((_, i) => (
            <div key={i} className="h-1 flex-1 overflow-hidden rounded-full" style={{ background: `${ink}1f` }}>
              <div className="h-full rounded-full transition-all" style={{ width: i <= idx ? "100%" : "0%", background: ink }} />
            </div>
          ))}
        </div>

        {/* tap zones */}
        <button aria-label="Vorige" onClick={prev} data-testid="slide-prev" className="absolute left-0 top-0 z-10 h-full w-1/3" />
        <button aria-label="Volgende" onClick={next} data-testid="slide-next" className="absolute right-0 top-0 z-10 h-2/3 w-2/3" />

        {/* slide-teller */}
        <span className="absolute right-4 top-6 z-0 text-xs font-semibold tabular-nums" style={{ color: ink, opacity: 0.5 }}>{idx + 1}/{total}</span>

        {/* Eén responsieve layoutregel voor ALLE slides:
            - tekstblok staat horizontaal én verticaal gecentreerd (my-auto)
            - bij meer tekst groeit het blok en scrolt het als geheel, zonder de centrering te verliezen
            - pt-12 houdt afstand tot de voortgangsbalk; het logo onderaan staat in flow, dus nooit overlap */}
        <div className="z-0 flex min-h-0 flex-1 flex-col overflow-y-auto px-7 pb-3 pt-12" data-testid={`slide-${idx + 1}`}>
          <div className="my-auto w-full text-center">
            {s.type === "intro" && (
              <>
                <h1 className="font-extrabold leading-tight" style={{ color: ink, fontSize: "2rem" }}>{product.naam}</h1>
                {product.werkzame_stof && (
                  <p className="mx-auto mt-3 w-fit rounded-full px-3 py-1 text-xs font-semibold" style={{ background: hoofd, color: ink }}>
                    {product.werkzame_stof}
                  </p>
                )}
                {product.vorm && (
                  <p className="mt-2 text-sm" style={body}>{product.vorm}{product.merk ? ` · ${product.merk}` : ""}</p>
                )}
                {product.afbeelding_url && (
                  <div className="mx-auto mt-5 w-fit max-w-full rounded-xl bg-white p-3 shadow-sm">
                    <img src={product.afbeelding_url} alt={product.naam} className="mx-auto max-h-[32vh] w-auto max-w-full object-contain" />
                  </div>
                )}
              </>
            )}

            {s.heading && !s.type && (
              <h2 className="mb-4 font-extrabold leading-tight" style={{ color: ink, fontSize: "1.6rem" }}>{s.heading}</h2>
            )}

            {s.blocks && s.blocks.map(([label, val, sub], i) => (
              <div key={i} className="mb-5 last:mb-0">
                <h3 className="font-bold" style={{ color: ink, fontSize: s.heading ? "0.95rem" : "1.35rem" }}>{label}</h3>
                {sub && <p className="mt-0.5 text-xs font-bold" style={{ color: ink }}>{sub}</p>}
                <p className="mt-1 text-[15px] leading-relaxed" style={body}>{val || "—"}</p>
              </div>
            ))}

            {s.list && (
              <ul className="space-y-4">
                {s.list.map((w, i) => (
                  <li key={i}>
                    <span className="mx-auto mb-2 block h-1.5 w-1.5 rounded-full" style={{ background: ink }} />
                    <p className="text-[15px] leading-relaxed" style={body}>{w}</p>
                  </li>
                ))}
              </ul>
            )}

            {s.body !== undefined && s.body !== null && !s.list && (
              <p className="text-[15px] leading-relaxed" style={body}>{s.body || "—"}</p>
            )}

            {s.type === "contact" && (
              <>
                <h2 className="font-extrabold leading-tight" style={{ color: ink, fontSize: "1.6rem" }}>Nog vragen over je geneesmiddel?</h2>
                <p className="mt-3 text-[15px]" style={body}>We helpen je graag verder in de apotheek</p>
                <div className="my-6 flex justify-center"><Logo big /></div>
                <div className="mx-auto w-full space-y-2 rounded-2xl px-5 py-4 text-sm" style={{ background: hoofd, color: ink }} data-testid="contact-card">
                  <p className="font-bold">{pharmacy.naam}</p>
                  {pharmacy.telefoon && <p className="flex items-center justify-center gap-2"><Phone className="h-4 w-4" />{pharmacy.telefoon}</p>}
                  {pharmacy.email && <p className="flex items-center justify-center gap-2"><Mail className="h-4 w-4" />{pharmacy.email}</p>}
                  {pharmacy.website && <p className="flex items-center justify-center gap-2"><Globe className="h-4 w-4" />{pharmacy.website}</p>}
                  {pharmacy.openingsuren && (
                    <p className="flex items-start justify-center gap-2 pt-1 text-xs leading-relaxed" style={{ opacity: 0.85 }} data-testid="openingsuren">
                      <Clock className="mt-0.5 h-4 w-4 flex-shrink-0" />
                      <span>{pharmacy.openingsuren}</span>
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* apotheek-logo onderaan elke slide — behalve de contactslide (daar staat het in het midden) */}
        {s.type !== "contact" && (
          <div className="z-0 flex justify-center pb-6 pt-1" data-testid="slide-logo-bottom">
            <Logo />
          </div>
        )}

        {/* desktop nav arrows */}
        {idx > 0 && (
          <button onClick={prev} className="absolute left-1 top-1/2 z-20 hidden -translate-y-1/2 rounded-full bg-white/80 p-1.5 shadow sm:block" style={{ color: ink }}>
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}
        {idx < total - 1 && (
          <button onClick={next} className="absolute right-1 top-1/2 z-20 hidden -translate-y-1/2 rounded-full bg-white/80 p-1.5 shadow sm:block" style={{ color: ink }}>
            <ChevronRight className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
}

const Center = ({ children }) => (
  <div className="flex min-h-screen items-center justify-center bg-slate-900">{children}</div>
);
